/**
 * #567 — la búsqueda por texto libre, UNA sola vez. Mitad WEB del gemelo.
 *
 * GEMELO EXACTO de `mcp-server/src/utils/toolSearch.js`: el cuerpo de los dos
 * archivos —todo menos este comentario de cabecera— es idéntico byte a byte, y el
 * test de paridad (`mcp-server/tests/tools/toolSearchLaneParity.test.js`) lo
 * verifica y además corre el MISMO dataset por las dos copias. Se duplica a mano,
 * igual que `rawTextFidelity.js` (#369) y `materialSplit.js` (#384), porque el
 * bundle de CRA no resuelve imports fuera de `src/` — ver el comentario de
 * `src/services/materialRequestService.js` sobre ese mismo borde. Si cambiás uno,
 * cambiá el otro.
 *
 * EN ESTE CARRIL lo consume `src/services/toolAssignmentService.js#listTools`, que
 * hasta #567 buscaba con `ilike('name', '%…%')` a secas: substring contiguo, un
 * solo campo y sensible a acentos. Medido: `search:"taladro rojo"` devolvía CERO
 * filas en la GUI que usa Jorge todos los días, mientras el agente ya devolvía DOS.
 *
 * `searchRows` y `taskSearchHaystack` (#572) viajan en esta copia sin consumidor
 * web todavía: la búsqueda de tareas de la GUI (`taskService.listTasks`) sigue con
 * su `.or(...ilike...)`, que filtra en la BASE y pagina ahí — cambiarla es mover
 * el paginador entero y es otro lote. Están acá porque el gemelo es byte a byte,
 * y de paso el día que se haga el cambio la definición del haystack ya es común.
 */

// Why: ilike is case-insensitive but accent-sensitive — "multimetro" never
// matches "Multímetro". We normalize both sides (NFD + strip diacritics +
// lowercase + collapse common punctuation) and tokenize so multi-word
// searches like "calibrador Mitutoyo" match "Calibrador Digital Mitutoyo
// 500-196" even with a word in the middle. Catalog is small (~15-100 rows),
// so the trade-off vs adding the unaccent extension + an index is fine.
export const TOOL_STOP_WORDS = new Set([
  'de','del','para','con','por','sin','el','la','los','las','un','una','unos','unas'
]);

export const normalizeForSearch = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    // #565: el punto y la coma tambien tienen que colapsar. Sin eso, una
    // descripcion que TERMINA la oracion en el adjetivo —"...carcasa roja."—
    // deja la palabra `roja.` con el punto pegado, y `roja.` no stemea a `roj`:
    // el match por concordancia se pierde en silencio, que es exactamente el
    // modo de falla que este issue vino a cerrar. Ambos lados de la comparacion
    // pasan por aca, asi que la normalizacion no puede desalinearse.
    //
    // #572: los signos de pregunta y de exclamacion, por la misma razon y con
    // el mismo modo de falla. Es el mismo bug de #565 un signo mas alla, y
    // aparecio en cuanto la busqueda dejo de recibir dos palabras tipeadas para
    // recibir una pregunta DICTADA: `¿cual era la tarea del compresor?` dejaba
    // `¿cual` y `compresor?` pegados a su signo, y `compresor?` no calza con
    // `compresor` — asi que la pregunta entera devolvia CERO. Ninguna
    // herramienta ni tarea lleva estos signos en su nombre, asi que sacarlos no
    // le quita poder de discriminacion a nada.
    .replace(/["'()\-_/.,;:¿?¡!]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const tokenizeForSearch = (s) =>
  normalizeForSearch(s)
    .split(' ')
    .filter(t => t.length > 0 && !TOOL_STOP_WORDS.has(t));

// #565 — the haystack used to be [name, serial_number, brand, model] ONLY, and
// that omission is measurable: a drill whose colour lives in `description`
// ("carcasa roja") was invisible to search:"taladro rojo", because `rojo` is
// not a stop word and nothing in those four fields carries it. There is no
// `color` column on `tools`, so `description` IS where the physical
// description lives — the one field a user actually describes a tool by.
// `category` joins for the same reason ("multimetro de medicion").
//
// Ubicacion: `location` es el texto libre legado ("bodega" a mano) y casi
// nunca lo que alguien escribe para una busqueda ("zona 1", "bodega santa
// ana") es lo que vive en esa columna. La ubicacion estructurada cuelga de
// `location_id` -> tool_locations(code, label); cada carril la trae y la
// cuelga de la fila como `location_rel` ANTES de llamar a esta funcion (ver
// toolManagementTools.js#listTools y toolAssignmentService.js#listTools), asi
// que el haystack solo necesita leerla. No hace falta `unaccent` ni `pg_trgm`
// en la base para esto: el catalogo de ubicaciones es chico y el filtro ya
// corre en JS, igual que el resto de esta busqueda.
export const toolSearchHaystack = (t) =>
  [
    t.name, t.serial_number, t.brand, t.model, t.description, t.category,
    t.location, t.location_rel?.label, t.location_rel?.code,
  ]
    .filter(Boolean)
    .map(normalizeForSearch)
    .join(' ');

// #565, segunda mitad del mismo bug — y la que NO estaba en el issue.
//
// Medido contra el Supabase local: la descripcion del Kestrel PMT-9001 dice
// "Carcasa ROJA con detalles negros". El usuario escribe "taladro ROJO". Meter
// `description` en el haystack no alcanza: `"carcasa roja".includes("rojo")`
// es false. En español el adjetivo concuerda con el sustantivo — la
// herramienta es un taladro (masculino) pero la carcasa es femenina — asi que
// la forma que el usuario tipea casi nunca es la forma que quedo escrita en la
// ficha. Un substring match no puede cerrar esa distancia.
//
// Stem deliberadamente romo: se le sacan la marca de plural y la vocal final
// de genero. Se compara por IGUALDAD de stems entre palabras COMPLETAS, no por
// prefijo: "man" (de "mano") no debe pescar "manometro". Y solo se usa como
// via ADICIONAL — el substring de siempre sigue valiendo — asi que nada de lo
// que ya matcheaba deja de matchear.
export const stemForSearch = (w) => {
  let s = w;
  if (s.length > 4 && s.endsWith('es')) s = s.slice(0, -2);
  else if (s.length > 3 && s.endsWith('s')) s = s.slice(0, -1);
  if (s.length > 3 && (s.endsWith('o') || s.endsWith('a'))) s = s.slice(0, -1);
  return s;
};

export const stemSet = (hay) => new Set(hay.split(' ').filter(Boolean).map(stemForSearch));

// Tolerancia a errores de tipeo (dictado por voz, autocorrector del celular,
// etc). Si un token no calzo ni por substring ni por stem, se acepta cuando
// hay una palabra COMPLETA en el haystack a distancia de edicion <=1 (una
// letra de mas, de menos, o cambiada). Solo para tokens de 5 letras o mas:
// en una palabra mas corta una sola letra de diferencia cambia la palabra
// entera y el falso positivo sale caro ("pala" no deberia pescar "mala").
// Implementacion JS pura — ningun carril tiene pg_trgm para esto, y aunque
// lo tuviera seguiria haciendo falta correr igual en el otro.
const isCloseMatch = (a, b) => {
  if (Math.abs(a.length - b.length) > 1) return false;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  let i = 0, j = 0, foundDifference = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      if (foundDifference) return false;
      foundDifference = true;
      if (shorter.length === longer.length) i++;
    } else {
      i++;
    }
    j++;
  }
  return true;
};

// ¿Este término del usuario está en esta herramienta? Substring crudo (el
// contrato de siempre), igualdad de stems contra alguna palabra completa, o
// -para palabras largas- una palabra a un error de tipeo de distancia.
export const tokenHits = (tok, hay, stems) => {
  if (hay.includes(tok) || stems.has(stemForSearch(tok))) return true;
  if (tok.length < 5) return false;
  return hay.split(' ').some((w) => w.length > 0 && isCloseMatch(tok, w));
};

// #572 — el haystack de una TAREA.
//
// Los mismos tres campos por los que busca la GUI (`src/services/taskService.js`
// → `listTasks`, que arma un `.or('task_number.ilike…,title.ilike…,
// description.ilike…')`): número, título y descripción. La regla de paridad es
// sobre los CAMPOS; la calidad del match la pone `searchRows`, que es
// estrictamente mejor que el `ilike` de la GUI (sin acentos, tokenizado,
// concordancia de género, degradado a parciales).
//
// `task_number` entra crudo: "T-2026-0031" normaliza a "t 2026 0031", así que
// tipear el número suelto —"la 31"— NO pesca la tarea por accidente, pero
// tipearlo entero sí. `project.title` NO entra: buscar "aire" no debe devolver
// las 40 tareas de un proyecto llamado "Aires acondicionados CIMA"; para eso
// está `get_tasks_by_project_number`.
export const taskSearchHaystack = (t) =>
  [t.task_number, t.title, t.description]
    .filter(Boolean)
    .map(normalizeForSearch)
    .join(' ');

/**
 * Filtra y rankea filas contra lo que el usuario tipeó.
 *
 * PURA: no consulta nada, no importa nada, no espera nada. Recibe las filas ya
 * traídas por el carril que la llama y devuelve el subconjunto en orden, SIN
 * paginar — el slice lo hace cada carril, porque cada uno tiene su propio
 * contrato de `count`.
 *
 * Nació en #565 atada a `tools` y se generalizó en #572, cuando hizo falta la
 * misma búsqueda sobre `tasks`. Lo único que cambia entre entidades es de qué
 * campos sale el texto (`haystack`) y con qué se desempata (`label`); todo lo
 * demás —normalización, tokenizado, token-AND, stems, degradado— es idéntico y
 * por eso NO se copia. Escribir un tercer buscador es lo que este archivo
 * existe para impedir.
 *
 * @param {Array<object>} rows     filas ya traídas por el carril.
 * @param {string} search          el texto crudo del usuario.
 * @param {(row:object)=>string} haystack  arma el texto buscable de una fila.
 * @param {(row:object)=>string} label     campo de desempate alfabético.
 * @returns {{tools: Array<object>, searchMode: ('exact'|'partial'|null),
 *   tokens: string[], partialMatches: (Map<string,{matched:string[],missing:string[]}>|null)}}
 *   `searchMode` es null cuando no había nada que buscar (search vacío o solo
 *   stop-words) — en ese caso `tools` es `rows` tal cual. 'exact' = todos los
 *   términos calzaron (el contrato pre-#565), y también el CERO honesto cuando
 *   no calzó nada. 'partial' = el AND estricto dio cero y esto son candidatos
 *   rankeados, que NUNCA deben presentarse como si fueran la respuesta.
 *
 *   La clave se llama `tools` por historia y se conserva a propósito: la
 *   renombrarla rompería a los tres consumidores vivos sin darle nada a nadie.
 */
export const searchRows = (rows, search, haystack, label = (r) => r.name) => {
  const all = rows || [];
  const tokens = tokenizeForSearch(search);
  if (tokens.length === 0) {
    return { tools: all, searchMode: null, tokens, partialMatches: null };
  }

  const scored = all.map((t) => {
    const hay = haystack(t);
    const stems = stemSet(hay);
    const matched = tokens.filter(tok => tokenHits(tok, hay, stems));
    return { tool: t, matched };
  });

  // Token-AND: every meaningful token from the user's phrasing must
  // appear in the haystack. "calibrador mitutoyo" → ["calibrador","mitutoyo"]
  // both have to be substrings of "calibrador digital mitutoyo 500 196 30".
  const exact = scored.filter(s => s.matched.length === tokens.length);
  if (exact.length > 0) {
    return { tools: exact.map(s => s.tool), searchMode: 'exact', tokens, partialMatches: null };
  }

  // #565 — DEGRADED PASS. The strict AND returning 0 is the expensive
  // failure mode here: the agent reads "no hay" and stops, when what
  // actually happened is that ONE token of the user's phrasing was not
  // in any row. Re-run with OR and rank by how many tokens each row
  // matched, so the agent can ask "encontré estos 2, ¿cuál?" instead.
  // Marked `partial` on the way out so the model never presents a
  // candidate as if it were the answer.
  const candidates = scored
    .filter(s => s.matched.length > 0)
    .sort((a, b) =>
      (b.matched.length - a.matched.length) ||
      String(label(a.tool) || '').localeCompare(String(label(b.tool) || '')));

  if (candidates.length === 0) {
    return { tools: [], searchMode: 'exact', tokens, partialMatches: null };
  }

  return {
    tools: candidates.map(s => s.tool),
    searchMode: 'partial',
    tokens,
    partialMatches: new Map(candidates.map(s => [
      s.tool.id,
      {
        matched: s.matched,
        missing: tokens.filter(tok => !s.matched.includes(tok)),
      },
    ])),
  };
};

/** `searchRows` sobre el haystack de una herramienta. El contrato de #565/#567. */
export const searchToolRows = (rows, search) => searchRows(rows, search, toolSearchHaystack);

// #572 — MULETILLAS DE PREGUNTA. Sólo para tareas, y por una razón medida.
//
// `TOOL_STOP_WORDS` se armó para lo que alguien TIPEA en una caja de búsqueda
// ("taladro rojo"). La búsqueda de tareas nació para el carril de voz, donde el
// usuario no tipea: DICTA una pregunta entera. El criterio de aceptación del
// issue es literal —"¿cuál era la tarea de los filtros del aire?"— y medido con
// la lista de siempre da `partial`, no `exact`: `cual`, `era` y `tarea` no son
// stop-words, no están en ninguna fila, y el token-AND falla por tres palabras
// que no dicen nada.
//
// `tarea` / `tareas` entran acá y NO en la lista compartida a propósito: dentro
// de una búsqueda de tareas la palabra no distingue nada (todas las filas lo
// son), pero en `list_tools` sí distingue, y ensuciar la lista común cambiaría
// la conducta de la búsqueda de herramientas sin que nadie lo pidiera.
export const TASK_QUERY_FILLER = new Set([
  'cual', 'cuales', 'que', 'quien', 'quienes', 'donde', 'cuando', 'como',
  'era', 'eran', 'es', 'son', 'fue', 'fueron', 'esta', 'estaba', 'hay', 'habia',
  'tarea', 'tareas', 'pendiente', 'pendientes',
  'buscar', 'busca', 'busca me', 'buscame', 'encontrar', 'encontra', 'encontrame',
  'mostrame', 'mostra', 'dame', 'decime', 'necesito', 'quiero', 'sobre', 'acerca',
  'y', 'o', 'a', 'al', 'en', 'mi', 'mis', 'su', 'sus', 'lo', 'se', 'me', 'te',
]);

/**
 * `searchRows` sobre el haystack de una tarea (#572). Desempata por título.
 *
 * Le saca las muletillas de pregunta ANTES de buscar. Si al sacarlas no queda
 * nada —el usuario dijo sólo "¿cuál era la tarea?"— se busca con el texto
 * crudo: degradar a "no había nada que buscar" devolvería la tabla entera
 * disfrazada de resultado, que es peor que un cero.
 */
export const searchTaskRows = (rows, search) => {
  const utiles = tokenizeForSearch(search).filter((t) => !TASK_QUERY_FILLER.has(t));
  const efectivo = utiles.length > 0 ? utiles.join(' ') : search;
  return searchRows(rows, efectivo, taskSearchHaystack, (t) => t.title);
};
